import { useIframe } from '@archipro-website/react-hooks';

import React, { useCallback, useEffect, useRef } from 'react';

import type {
    IFrameMessage,
    IFrameMessageDispatcher,
    BusinessManagerMessages,
    Navigate,
    Initialise,
    PreviewPatch,
} from '@modules/business-manager/types/business-manager';
import { useDataRefresh } from '@modules/root/hook/use-data-refresh';
import { useNavigate, useMatches } from 'react-router';
import { useSetAtom } from 'jotai';

import { previewPatchAtom } from '@modules/business-manager/state/preview-patch';

interface BusinessManagerListenerProps {
    /**
     * Whether this preview renders the era that reads `previewPatchAtom` — the
     * revamp. The legacy page never reads it, so a patch there would be
     * acknowledged and then discarded, leaving the author looking at the value
     * they just replaced. Defaults to false so a caller that says nothing gets
     * the safe, always-correct behaviour rather than the silent one.
     */
    canApplyPatch?: boolean;
}

/** Long enough to let one upload's burst of patches settle into a single refresh. */
const LEGACY_REFRESH_DEBOUNCE_MS = 500;
/**
 * The patch is acknowledged before this refresh runs, so the sender's watchdog
 * will not order a retry — a rejected revalidation would otherwise leave the
 * author looking at pre-edit data with nothing to tell them. One retry covers
 * the blip without turning a genuinely broken loader into a loop.
 */
const LEGACY_REFRESH_RETRIES = 1;
const LEGACY_REFRESH_RETRY_MS = 1000;

const BusinessManagerListener: React.FC<BusinessManagerListenerProps> = ({
    canApplyPatch = false,
}) => {
    const navigate = useNavigate();
    const matches = useMatches();
    const currentPath = matches[matches.length - 1]?.pathname;

    const { refresh } = useDataRefresh();
    const setPreviewPatch = useSetAtom(previewPatchAtom);

    // One upload sends several patches — the optimistic image, the colour
    // derived from it, then the saved value once the mutation answers — and
    // only the last reflects what the server now holds. Refreshing on each
    // would re-run every loader three times to land one field, so coalesce on
    // the trailing edge and fetch once, after the burst.
    const legacyRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const mounted = useRef(true);

    const runLegacyRefresh = useCallback(async () => {
        for (let attempt = 0; attempt <= LEGACY_REFRESH_RETRIES; attempt++) {
            try {
                await refresh();
                return;
            } catch {
                if (attempt === LEGACY_REFRESH_RETRIES || !mounted.current) {
                    return;
                }
                await new Promise((resolve) =>
                    setTimeout(resolve, LEGACY_REFRESH_RETRY_MS),
                );
                if (!mounted.current) {
                    return;
                }
            }
        }
    }, [refresh]);

    const refreshAfterBurst = useCallback(() => {
        if (legacyRefreshTimer.current) {
            clearTimeout(legacyRefreshTimer.current);
        }
        legacyRefreshTimer.current = setTimeout(() => {
            legacyRefreshTimer.current = null;
            void runLegacyRefresh();
        }, LEGACY_REFRESH_DEBOUNCE_MS);
    }, [runLegacyRefresh]);

    useEffect(
        () => () => {
            mounted.current = false;
            if (legacyRefreshTimer.current) {
                clearTimeout(legacyRefreshTimer.current);
            }
        },
        [],
    );

    const handler = useCallback(
        (
            message: IFrameMessage<BusinessManagerMessages>,
            dispatch: IFrameMessageDispatcher<
                IFrameMessage<BusinessManagerMessages>
            >,
        ) => {
            switch (message.type) {
                case 'initialise': {
                    const content = message.content as Initialise;
                    // Replace so preview tab changes do not push iframe history
                    // entries — otherwise the browser back button navigates the
                    // iframe (often into an aborted loader / NetworkError flash)
                    // instead of the BM parent.
                    navigate(content.route, { replace: true });

                    dispatch({
                        type: 'initialiseDone',
                        content,
                    });
                    break;
                }
                case 'navigate': {
                    const content = message.content as Navigate;
                    navigate(content.route, { replace: true });
                    break;
                }

                case 'refresh': {
                    // Await the revalidation so `refreshDone` means the preview
                    // is showing the new data, not that we started fetching it.
                    // Loader data is authoritative once it lands, so any pending
                    // optimistic overrides are dropped at the same moment.
                    void refresh().then(() => {
                        setPreviewPatch({});
                        dispatch({ type: 'refreshDone' });
                    });
                    break;
                }

                case 'patch': {
                    // The legacy page reads loader data only, so the override
                    // below would go nowhere. Re-fetch instead: slower than a
                    // patch, but it is what this page understands, and it is
                    // what the author needs to see.
                    if (!canApplyPatch) {
                        refreshAfterBurst();
                        // Acknowledged because it *is* handled — a refresh is
                        // coming. Staying silent would leave the sender's
                        // watchdog to order one anyway, a second later and a
                        // second time.
                        dispatch({ type: 'patchDone' });
                        break;
                    }

                    const content = message.content as PreviewPatch;
                    setPreviewPatch((current) => ({
                        ...current,
                        ...content,
                        initial: { ...current.initial, ...content.initial },
                    }));
                    // Acknowledge, so an unanswered patch is a detectable
                    // condition rather than a preview that quietly kept showing
                    // the old value. The sender falls back to a full refresh.
                    dispatch({ type: 'patchDone' });
                    break;
                }
            }
        },
        [refresh, navigate, setPreviewPatch, canApplyPatch, refreshAfterBurst],
    );

    const [dispatch] =
        useIframe<IFrameMessage<BusinessManagerMessages>>(handler);

    useEffect(() => {
        // Always ack path changes. After an iframe src reload the child remounts
        // while the parent may still send `navigate`; previously we only acked
        // after initialise, which left the parent stuck at opacity 0.
        dispatch({
            type: 'navigateDone',
            content: {
                route: currentPath ?? '',
            },
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPath]);

    return <></>;
};

export default BusinessManagerListener;
